import React, { useEffect, useState } from "react";
import { Form, Button, Card, Row, Col, Badge, Spinner } from "react-bootstrap";
import { FaUserPlus, FaEye, FaCheck, FaUndo } from "react-icons/fa";
import { BiShareAlt } from "react-icons/bi";
import Select from "react-select";
import { FaUserTag } from "react-icons/fa";
import { FcDepartment } from "react-icons/fc";
import { MdAddLocationAlt } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import SessionPreview from "./SessionPreview";
import { FaSearch } from "react-icons/fa";
import {
  searchAreaByDepartmentApi,
  SearchAuditorsApi,
  SearchDepartmentByLocationApi,
  SearchLocationApi,
} from "../../../services/SearchService";
import { toast } from "react-toastify";
import { createAuditSession } from "../../../services/AuditServices";

const AuditSessionAdd = () => {
  const navigate = useNavigate();
  //session
  const [createLoading, setCreateLoading] = useState(false);
  const [sessionName, setSessionName] = useState("");
  const [formErrors, setFormErrors] = useState({});

  //location
  const [locationOption, setLocationoption] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  //department
  const [departmentOption, setDepartmentOption] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  //area
  const [areaOption, setAreaOption] = useState([]);
  const [areaLoading, setAreaLoading] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState([]);

  //auditor
  const [auditorOption, setAuditorOption] = useState([]);
  const [auditorLoading, setAuditorLoading] = useState(false);
  const [selectedAuditor, setSelectedAuditor] = useState("");

  //edit
  const [editMode, setEditMode] = useState(false);
  const [editAuditorId, setEditAuditorId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [assignedAuditors, setAssignedAuditors] = useState([]);

  const getLocation = () => {
    setLocationLoading(true);
    SearchLocationApi()
      .then((res) => {
        setLocationoption(res?.data?.results || []);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setLocationLoading(false);
      });
  };

  const getAuditor = () => {
    setAuditorLoading(true);
    SearchAuditorsApi()
      .then((res) => {
        const data = res?.data?.data || [];
        setAuditorOption(
          data?.map((item) => ({
            label: item?.fullname || "",
            value: item?.id || "",
          })) || []
        );
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setAuditorLoading(false);
      });
  };

  const getDepartment = () => {
    setDepartmentLoading(true);
    SearchDepartmentByLocationApi(selectedLocation?.value || "")
      .then((res) => {
        setDepartmentOption(res?.data?.results || []);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setDepartmentLoading(false);
      });
  };

  const fetchAreasByDept = async (dept_id) => {
    const res = await searchAreaByDepartmentApi(dept_id);
    const data = res?.data?.results || [];
    return data.map((item) => ({
      label: item.label || "",
      value: item.value || "",
      department_id: dept_id,
    }));
  };

  useEffect(() => {
    getLocation();
    getAuditor();
  }, []);

  useEffect(() => {
    if (selectedLocation?.value) {
      handleResetLocation();
      getDepartment();
    }
  }, [selectedLocation]);

  const availableAuditors = editMode
    ? auditorOption
    : auditorOption.filter(
        (a) => !assignedAuditors.some((assigned) => assigned.id === a.value)
      );

  const assignedAreaIds = assignedAuditors.flatMap((aud) =>
    aud.areas.map((a) => a.area_id)
  );

  const filteredAreas = areaOption
    .filter((area) => selectedDepartments.includes(area.department_id))
    .filter((area) =>
      area.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((area) => {
      if (editMode) {
        const editingAuditor = assignedAuditors.find(
          (a) => a.id === editAuditorId
        );
        const editingAreaIds =
          editingAuditor?.areas.map((a) => a.area_id) || [];
        if (editingAreaIds.includes(area.value)) return true;
      }
      return !assignedAreaIds.includes(area.value);
    });

  const handleDepartmentChange = async (selectedOptions) => {
    const newDeptIds = selectedOptions.map((opt) => opt.value);
    if (newDeptIds.length > 0 && formErrors.department) {
      setFormErrors((prevErr) => ({ ...prevErr, department: "" }));
    }

    const addedDeptIds = newDeptIds.filter(
      (newId) => !selectedDepartments.includes(newId)
    );

    if (addedDeptIds.length > 0) {
      setAreaLoading(true);
      try {
        const allNewAreas = await Promise.all(
          addedDeptIds.map((deptId) => fetchAreasByDept(deptId))
        );

        setAreaOption((prev) => {
          const existingIds = new Set(prev.map((a) => a.value));
          const newUniqueAreas = allNewAreas
            .flat()
            .filter((a) => !existingIds.has(a.value));
          return [...prev, ...newUniqueAreas];
        });
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      } finally {
        setAreaLoading(false);
      }
    }

    setSelectedDepartments(newDeptIds);
    setSelectedAreas([]);
  };

  const handleAreaToggle = (areaId) => {
    setSelectedAreas((prev) => {
      let updated;
      if (prev.includes(areaId)) {
        updated = prev.filter((id) => id !== areaId);
      } else {
        updated = [...prev, areaId];
      }

      if (updated.length > 0 && formErrors.area) {
        setFormErrors((prevErr) => ({ ...prevErr, area: "" }));
      }

      return updated;
    });
  };

  const handleAddAuditor = () => {
    let errors = {};
    let hasError = false;

    if (!selectedAuditor) {
      errors.auditor = "Please select an auditor.";
      hasError = true;
    }

    if (!selectedDepartments || selectedDepartments.length === 0) {
      errors.department = "Please select at least one department.";
      hasError = true;
    }

    if (!selectedAreas || selectedAreas.length === 0) {
      errors.area = "Please select at least one area.";
      hasError = true;
    }

    setFormErrors({ ...formErrors, ...errors });

    if (hasError) {
      return;
    }

    const auditorObj = auditorOption.find((a) => a.value === selectedAuditor);

    const selectedAreaObjects = selectedAreas.map((areaId) => {
      const areaObj = areaOption.find((a) => a.value === areaId);
      return {
        area_id: areaObj.value,
        department_id: areaObj.department_id,
      };
    });

    setAssignedAuditors((prev) => [
      ...prev,
      {
        id: auditorObj?.value || "",
        departments: selectedDepartments,
        areas: selectedAreaObjects,
      },
    ]);

    handleResetForm();
  };

  const createSession = (data) => {
    setCreateLoading(true);
    createAuditSession(data)
      .then((res) => {
        toast.success("Session Created Successfully");
        handleResetLocation();
        setSelectedLocation(null);
        setSessionName("");
        navigate("/auditsession");
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setCreateLoading(false);
      });
  };

  const handleSubmit = () => {
    let errors = {};
    let hasError = false;

    if (!selectedLocation) {
      errors.location = "Please select a location.";
      hasError = true;
    }

    if (!sessionName) {
      errors.sessionName = "Please enter session name.";
      hasError = true;
    }

    setFormErrors({ ...formErrors, ...errors });

    if (hasError) {
      return;
    }

    const auditorDetails = assignedAuditors.map((aud) => {
      const deptMap = {};

      aud.areas.forEach(({ area_id, department_id }) => {
        if (!deptMap[department_id]) {
          deptMap[department_id] = [];
        }
        deptMap[department_id].push(area_id);
      });

      const auditorPayload = { auditor: aud.id };
      aud.departments.forEach((deptId, idx) => {
        auditorPayload[`department_${idx}`] = {
          id: deptId,
          area: deptMap[deptId] || [],
        };
      });

      return auditorPayload;
    });

    const finalPayload = {
      name: sessionName,
      location: selectedLocation?.value || "",
      assign_data: auditorDetails,
    };

    createSession(finalPayload);
  };

  const handleResetForm = () => {
    setSelectedAuditor("");
    setSelectedDepartments([]);
    setSelectedAreas([]);
    setSearchTerm("");
    setEditMode(false);
    setEditAuditorId(null);
    setFormErrors({});
    // setAreaOption([])
  };

  const handleResetLocation = () => {
    setDepartmentOption([]);
    setSelectedDepartments([]);
    setAreaOption([]);
    setSelectedAreas([]);
    setSearchTerm("");
    setEditMode(false);
    setEditAuditorId(null);
    setSelectedAuditor("");
    setAssignedAuditors([]);
  };

  const handleSaveEdit = () => {
    const updatedAreaObjects = selectedAreas.map((areaId) => {
      const areaObj = areaOption.find((a) => a.value === areaId);
      return {
        area_id: areaObj.value,
        department_id: areaObj.department_id,
      };
    });

    setAssignedAuditors((prev) =>
      prev.map((aud) =>
        aud.id === editAuditorId
          ? {
              ...aud,
              departments: selectedDepartments,
              areas: updatedAreaObjects,
            }
          : aud
      )
    );

    // ✅ FULL RESET to exit edit mode
    handleResetForm();
  };

  const handleEditAuditor = (auditor) => {
    // ✅ Extract department IDs from existing auditor
    const deptIds = auditor.departments;

    // ✅ Extract only area_id for selection UI
    const areaIds = auditor.areas.map((a) => a.area_id);

    setSelectedAuditor(auditor.id.toString());
    setSelectedDepartments(deptIds);
    setSelectedAreas(areaIds);

    setEditMode(true);
    setEditAuditorId(auditor.id);
  };

  const handleDeleteAuditor = (auditorId) => {
    setAssignedAuditors((prev) => prev.filter((a) => a.id !== auditorId));
  };

  const customSelectStyles = {
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#f3f6f9",
      borderRadius: "8px",
      padding: "2px 6px",
      border: "1px solid #d1d5db", // subtle border
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#374151 !important", // dark text for better readability
      fontWeight: 500,
      fontSize: "0.85rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#6b7280", // soft gray icon
      ":hover": {
        color: "#ef4444 !important", // red on hover for remove
      },
    }),
  };

  return (
    <>
      <div className="mb-3">
        <h2 className="text-primary">Create Audit Session</h2>
        <ol className="breadcrumb breadcrumb-arrow mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/auditsession">Session List</Link>
          </li>
          <li className="breadcrumb-item active">Create Audit Session</li>
        </ol>
      </div>

      <div className="container-fluid p-0">
        <Row>
          <Col md={8}>
            <div className="card mb-2">
              <div className="card-body d-flex justify-content-between gap-3">
                <div className="flex-fill">
                  <label className="form-label text-dark">
                    <MdAddLocationAlt className="me-1" />
                    Session Location (Fixed)
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <Select
                    classNamePrefix="select"
                    className="react-select"
                    options={locationOption}
                    value={selectedLocation}
                    onChange={(selected) => {
                      if (selected?.value) {
                        setFormErrors({ ...formErrors, location: "" });
                      }
                      setSelectedLocation(selected);
                    }}
                    isLoading={locationLoading}
                  />
                  {formErrors?.location && (
                    <small className="text-danger">
                      {formErrors?.location}
                    </small>
                  )}
                </div>
                <div className="flex-fill">
                  <label className="form-label text-dark">
                    Session Name<span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Session-01"
                    className="form-control"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        setFormErrors({ ...formErrors, sessionName: "" });
                      }
                      setSessionName(value||'');
                    }}
                    value={sessionName}
                  />
                  {formErrors?.sessionName && (
                    <small className="text-danger">
                      {formErrors?.sessionName}
                    </small>
                  )}
                </div>
              </div>
            </div>
            <Card className={`mb-3 ${editMode ? "text-bg-warning-soft" : ""}`}>
              <Card.Body>
                <div className="mb-3">
                  <label className="form-label text-dark">
                    <FaUserTag className="me-1" />
                    Auditor
                  </label>
                  <Select
                    isLoading={auditorLoading}
                    classNamePrefix="select"
                    className="react-select"
                    placeholder="Select Auditor"
                    options={availableAuditors.map((a) => ({
                      value: a.value.toString(),
                      label: a.label,
                    }))}
                    value={
                      selectedAuditor
                        ? {
                            value: selectedAuditor,
                            label: auditorOption.find(
                              (a) => a.value.toString() === selectedAuditor
                            )?.label,
                          }
                        : null
                    }
                    onChange={(selected) => {
                      if (selected?.value) {
                        setFormErrors({ ...formErrors, auditor: "" });
                      }
                      setSelectedAuditor(selected.value);
                    }}
                  />
                  {formErrors?.auditor && (
                    <small className="text-danger">{formErrors?.auditor}</small>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label text-dark">
                    <FcDepartment className="me-1" />
                    Department
                  </label>
                  <Select
                    classNamePrefix="select"
                    className="react-select"
                    isMulti
                    placeholder="Select Department(s)"
                    options={departmentOption}
                    value={departmentOption.filter((d) =>
                      selectedDepartments.includes(d.value)
                    )}
                    onChange={handleDepartmentChange}
                    isLoading={departmentLoading}
                    styles={customSelectStyles}
                  />
                  {formErrors?.department && (
                    <small className="text-danger">
                      {formErrors?.department}
                    </small>
                  )}
                </div>

                <div>
                  <div>
                    <label className="form-label text-dark">
                      <BiShareAlt className="me-1" />
                      Search Area
                    </label>
                    <div className="position-relative">
                      <div
                        className="position-absolute top-50 translate-middle-y text-primary"
                        style={{ left: "15px", pointerEvents: "none" }}
                      >
                        <FaSearch className="text-dark" />
                      </div>

                      <input
                        className="form-control px-5"
                        type="search"
                        placeholder="Search Area by area name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    {formErrors?.area && (
                      <small className="text-danger">{formErrors?.area}</small>
                    )}
                  </div>

                  {filteredAreas.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="selectAll"
                          checked={
                            selectedAreas.length === filteredAreas.length &&
                            filteredAreas.length > 0
                          }
                          onChange={() => {
                            if (selectedAreas.length === filteredAreas.length) {
                              setSelectedAreas([]);
                            } else {
                              setSelectedAreas(
                                filteredAreas.map((a) => a.value)
                              );
                              setFormErrors({ ...formErrors, area: "" });
                            }
                          }}
                        />
                        <label htmlFor="selectAll" className="form-check-label">
                          Select All Areas
                        </label>
                      </div>
                      <div
                        className="d-flex gap-2"
                        style={{ fontSize: "0.8rem" }}
                      >
                        <div>
                          <span className="text-dark fw-medium">
                            Selected Area
                          </span>
                          <span className="text-darker fw-medium">
                            ({selectedAreas?.length || 0})
                          </span>
                        </div>
                        <div>
                          <span className="text-dark fw-medium">
                            Total Area
                          </span>
                          <span className="text-darker fw-medium">
                            ({filteredAreas?.length || 0})
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className="d-flex flex-wrap mt-2 gap-2 overflow-auto scroll-container"
                    style={{ maxHeight: "400px" }}
                  >
                    {filteredAreas.map((area) => (
                      <Form.Check
                        key={area.value}
                        className="bg-light rounded-2 py-1 px-2 cursor-pointer small-checkbox"
                        type="checkbox"
                        id={`area-${area.value}`}
                        label={area.label}
                        checked={selectedAreas.includes(area.value)}
                        onChange={() => handleAreaToggle(area.value)}
                      />
                    ))}

                    {filteredAreas.length === 0 && (
                      <div className="text-dark small">
                        No available areas for selected department(s)
                      </div>
                    )}
                  </div>
                </div>

                {/* Add Button */}
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button variant="outline-secondary" onClick={handleResetForm}>
                    <FaUndo /> Reset Form
                  </Button>

                  {editMode ? (
                    <>
                      <Button variant="secondary" onClick={handleResetForm}>
                        Cancel
                      </Button>
                      <Button variant="warning" onClick={handleSaveEdit}>
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <Button variant="dark" onClick={handleAddAuditor}>
                      + Add Auditor
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <SessionPreview
              assignedAuditors={assignedAuditors}
              dummyDepartments={departmentOption}
              onEdit={handleEditAuditor}
              onDelete={handleDeleteAuditor}
              dummyAreas={areaOption}
              auditorOption={auditorOption}
            />
          </Col>
        </Row>

        <div className="text-end mt-2">
          <Button
            disabled={
              assignedAuditors.length == 0 ||
              createLoading
            }
            variant="primary"
            onClick={handleSubmit}
          >
            {createLoading && (
              <Spinner animation="border" size="sm" className="me-1" />
            )}
            Submit
          </Button>
        </div>
      </div>
    </>
  );
};

export default AuditSessionAdd;
